import type { User } from '@/types/user';
import { apiFetch } from '@/lib/api';

export interface AuthProvidersConfig {
  providers: Array<{
    type: string;
    displayName: string;
    enabled: boolean;
  }>;
  magicLinkEnabled: boolean;
}

export class AuthService {
  // Registration method removed - account creation is disabled

  async login(input: { email: string; password: string; rememberMe?: boolean }): Promise<User> {
    const res = await apiFetch(`/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      skipRefresh: true,
      body: JSON.stringify(input),
    });
    if (!res.ok) throw new Error('Login failed');
    const data = await res.json();
    return data.data as User;
  }

  async logout(): Promise<void> {
    const res = await apiFetch(`/api/auth/logout`, { method: 'POST', skipRefresh: true });
    if (!res.ok) throw new Error('Logout failed');
  }

  async refresh(): Promise<User> {
    const res = await apiFetch(`/api/auth/refresh`, { method: 'POST', skipRefresh: true });
    if (!res.ok) throw new Error('Refresh failed');
    const data = await res.json();
    return data.data as User;
  }

  async me(skipRedirect = false): Promise<User> {
    const res = await apiFetch(`/api/auth/me`, { skipRedirect });
    if (!res.ok) throw new Error('Not authenticated');
    const data = await res.json();
    return data.data as User;
  }

  /** Fetch enabled auth providers config */
  async getProviders(): Promise<AuthProvidersConfig> {
    const res = await apiFetch('/api/auth/providers', { skipRefresh: true });
    if (!res.ok) return { providers: [{ type: 'local', displayName: 'Email & Password', enabled: true }], magicLinkEnabled: false };
    const data = await res.json();
    return data.data as AuthProvidersConfig;
  }

  /** Request a magic link email */
  async requestMagicLink(email: string): Promise<void> {
    const res = await apiFetch('/api/auth/magic-link/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      skipRefresh: true,
      body: JSON.stringify({ email }),
    });
    if (!res.ok) throw new Error('Failed to send magic link');
  }

  /** Verify a magic link token and log in */
  async verifyMagicLink(token: string): Promise<User> {
    const res = await apiFetch('/api/auth/magic-link/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      skipRefresh: true,
      body: JSON.stringify({ token }),
    });
    if (!res.ok) throw new Error('Invalid or expired magic link');
    const data = await res.json();
    return data.data as User;
  }
}


